import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { FormControlLabel, Radio as MuiRadio, useRadioGroup } from '@mui/material'

import { RADIO_TYPES } from './radio-button.constants'
import { styles } from './radio-button.styles'
import RadioButtonLabel from './radio-button-label.component'

export const RadioButton = ({
  type,
  disabled,
  checked,
  value,
  label,
  largeLabel,
  helperText,
  avatar,
  icon,
  warning,
  description,
  onChange,
  sx,
  error,
}) => {
  const [isChecked, setIsChecked] = useState(checked)
  const radioGroup = useRadioGroup()

  const handleChange = (event) => {
    setIsChecked(!isChecked)
    onChange?.(event)
  }

  const checkedState = useMemo(() => {
    if (radioGroup) {
      return radioGroup.value === value
    }
    return checked !== undefined ? checked : isChecked
  }, [checked, isChecked, radioGroup, value])

  const radioTypes = {
    [RADIO_TYPES.SIMPLE]: {
      Component: MuiRadio,
      itemProps: {
        color: 'primary',
        inputProps: { 'aria-label': label },
        size: 'small',
        sx: [sx, styles.simpleRadio],
      },
    },
    [RADIO_TYPES.LABEL]: {
      Component: FormControlLabel,
      itemProps: {
        label: RadioButtonLabel({
          checked: checkedState,
          label,
          warning,
        }),
        sx,
        control: <MuiRadio size="small" color="primary" />,
      },
    },
    [RADIO_TYPES.BOX]: {
      Component: FormControlLabel,
      itemProps: {
        sx: [
          sx,
          styles.box,
          (largeLabel || (label && helperText && description)) && styles.boxLarge,
          checkedState && styles.boxChecked,
          disabled && styles.boxDisabled,
          error && styles.boxError,
        ],
        label: RadioButtonLabel({
          checked: checkedState,
          avatar,
          icon,
          label,
          largeLabel,
          helperText,
          disabled,
          description,
          warning,
        }),
        control: <MuiRadio sx={styles.radio} size="small" color="primary" />,
        labelPlacement: 'start',
      },
    },
  }
  const radioComponent = ({ Component, itemProps }) => (
    <Component
      value={value}
      disabled={disabled}
      onChange={handleChange}
      checked={checkedState}
      {...itemProps}
      sx={[
        styles.formControlLabel,
        ...(Array.isArray(itemProps.sx) ? itemProps.sx : [itemProps.sx]),
      ]}
    />
  )

  return radioComponent(radioTypes[type])
}

RadioButton.propTypes = {
  type: PropTypes.oneOf(Object.values(RADIO_TYPES)),
  value: PropTypes.string,
  label: PropTypes.string,
  description: PropTypes.string,
  largeLabel: PropTypes.string,
  helperText: PropTypes.string,

  avatar: PropTypes.shape({
    item: PropTypes.shape({ src: PropTypes.string, alt: PropTypes.string }),
  }),
  icon: PropTypes.elementType,

  disabled: PropTypes.bool,
  checked: PropTypes.bool,
  warning: PropTypes.string,
  error: PropTypes.bool,
  onChange: PropTypes.func,

  sx: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.func, PropTypes.array]),
}
RadioButton.defaultProps = {
  type: RADIO_TYPES.BOX,
  sx: {},
}

export default RadioButton
